// =============================================================================
// ZONE EDITOR 2D - MAIN COMPONENT
// =============================================================================

import { useRef, useState, useCallback, useEffect } from 'react'
import type { Zone, Point2D, WorkspaceConfig } from '../../types'
import type { EditorState, ZoneEditor2DProps } from './types'
import {
  worldToCanvas,
  canvasToWorld,
  getScale,
  isPointInPolygon,
  getPolygonCenter,
  createRectangle,
  distance,
  formatDistance
} from './utils'

const ZONE_COLORS_LIST = [
  { fill: 'rgba(239, 68, 68, 0.3)', stroke: 'rgb(239, 68, 68)' },
  { fill: 'rgba(34, 197, 94, 0.3)', stroke: 'rgb(34, 197, 94)' },
  { fill: 'rgba(59, 130, 246, 0.3)', stroke: 'rgb(59, 130, 246)' },
  { fill: 'rgba(168, 85, 247, 0.3)', stroke: 'rgb(168, 85, 247)' },
  { fill: 'rgba(234, 179, 8, 0.3)', stroke: 'rgb(234, 179, 8)' },
  { fill: 'rgba(236, 72, 153, 0.3)', stroke: 'rgb(236, 72, 153)' },
  { fill: 'rgba(20, 184, 166, 0.3)', stroke: 'rgb(20, 184, 166)' },
  { fill: 'rgba(249, 115, 22, 0.3)', stroke: 'rgb(249, 115, 22)' },
]

const LIDAR_MAX_RANGE = 12000 // 12m in mm

export function ZoneEditor2D({
  zones,
  workspace,
  onCreateZone,
  onUpdateZone,
  onDeleteZone,
  onSelectZone,
  onUpdateWorkspace,
  selectedZone
}: ZoneEditor2DProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sideCanvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Refs for immediate drag state (avoids React async state issues)
  const dragStateRef = useRef<{
    element: 'lidar' | 'zone' | 'handle' | null
    handleIndex: number
    startPos: Point2D
    zone: Zone | null
  }>({ element: null, handleIndex: -1, startPos: { x: 0, y: 0 }, zone: null })

  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 })
  const [state, setState] = useState<EditorState>({
    zoom: 1,
    panOffset: { x: 0, y: 0 },
    tool: 'select',
    isDrawing: false,
    drawPoints: [],
    hoveredZone: null,
    selectedZone: selectedZone,
    draggedElement: null,
    dragOffset: { x: 0, y: 0 },
    dragHandleIndex: -1,
    hoveredHandle: -1
  })

  const [newZoneName, setNewZoneName] = useState('')
  const [showNameDialog, setShowNameDialog] = useState(false)
  const [pendingPoints, setPendingPoints] = useState<Point2D[]>([])

  // Preview state for dragging (local only, not sent to server)
  const [dragPreview, setDragPreview] = useState<{
    lidar?: { x: number; y: number }
    zonePoints?: Point2D[]
  } | null>(null)

  // Sync selectedZone from props
  useEffect(() => {
    setState(prev => ({ ...prev, selectedZone }))
  }, [selectedZone])

  // Resize observer
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0]
      if (entry) {
        setCanvasSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        })
      }
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [])

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Clear
    ctx.fillStyle = '#1a1a2e'
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height)

    // Draw grid
    drawGrid(ctx, workspace, canvasSize, state.zoom, state.panOffset)

    // Draw LIDAR range circle (use preview position if dragging)
    const lidarRangePos = dragPreview?.lidar || { x: workspace.lidar_x, y: workspace.lidar_y }
    drawLidarRange(ctx, workspace, canvasSize, state.zoom, state.panOffset, lidarRangePos)

    // Draw zones
    zones.forEach((zone, index) => {
      // Use preview points if dragging this zone
      let points = zone.geometry_points || []
      const isDraggingThisZone = (state.selectedZone?.id === zone.id || dragStateRef.current.zone?.id === zone.id)
      if (isDraggingThisZone && dragPreview?.zonePoints) {
        points = dragPreview.zonePoints
      }
      if (points.length < 3) return

      const canvasPoints = points.map(p =>
        worldToCanvas(p, workspace, canvasSize.width, canvasSize.height, state.zoom, state.panOffset)
      )

      const colors = ZONE_COLORS_LIST[index % ZONE_COLORS_LIST.length]
      const isSelected = state.selectedZone?.id === zone.id
      const isHovered = state.hoveredZone?.id === zone.id

      // Fill
      ctx.beginPath()
      ctx.moveTo(canvasPoints[0].x, canvasPoints[0].y)
      for (let i = 1; i < canvasPoints.length; i++) {
        ctx.lineTo(canvasPoints[i].x, canvasPoints[i].y)
      }
      ctx.closePath()
      ctx.fillStyle = isSelected ? colors.fill.replace('0.3', '0.5') : colors.fill
      ctx.fill()

      // Stroke
      ctx.strokeStyle = colors.stroke
      ctx.lineWidth = isSelected ? 3 : isHovered ? 2 : 1
      ctx.stroke()

      // Label
      const center = getPolygonCenter(canvasPoints)
      ctx.fillStyle = '#fff'
      ctx.font = '12px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(zone.name, center.x, center.y)

      // Draw handles if selected
      if (isSelected && points.length > 0) {
        canvasPoints.forEach((p, i) => {
          const isHoveredHandle = state.hoveredHandle === i
          ctx.beginPath()
          ctx.arc(p.x, p.y, isHoveredHandle ? 8 : 6, 0, Math.PI * 2)
          ctx.fillStyle = isHoveredHandle ? '#fff' : colors.stroke
          ctx.fill()
          ctx.strokeStyle = isHoveredHandle ? colors.stroke : '#fff'
          ctx.lineWidth = 2
          ctx.stroke()
        })
      }
    })

    // Draw LIDAR marker (use preview position if dragging)
    const lidarPos = dragPreview?.lidar || { x: workspace.lidar_x, y: workspace.lidar_y }
    drawLidarMarker(ctx, workspace, canvasSize, state.zoom, state.panOffset, lidarPos)

    // Draw current drawing
    if (state.isDrawing && state.drawPoints.length > 0) {
      const canvasPoints = state.drawPoints.map(p =>
        worldToCanvas(p, workspace, canvasSize.width, canvasSize.height, state.zoom, state.panOffset)
      )

      ctx.beginPath()
      ctx.moveTo(canvasPoints[0].x, canvasPoints[0].y)
      for (let i = 1; i < canvasPoints.length; i++) {
        ctx.lineTo(canvasPoints[i].x, canvasPoints[i].y)
      }

      if (state.tool === 'rectangle' && canvasPoints.length === 2) {
        // Preview rectangle
        const [p1, p2] = canvasPoints
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)'
        ctx.lineWidth = 2
        ctx.setLineDash([5, 5])
        ctx.strokeRect(
          Math.min(p1.x, p2.x),
          Math.min(p1.y, p2.y),
          Math.abs(p2.x - p1.x),
          Math.abs(p2.y - p1.y)
        )
        ctx.setLineDash([])
      } else {
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)'
        ctx.lineWidth = 2
        ctx.setLineDash([5, 5])
        ctx.stroke()
        ctx.setLineDash([])
      }

      // Draw points
      canvasPoints.forEach(p => {
        ctx.beginPath()
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2)
        ctx.fillStyle = 'rgb(59, 130, 246)'
        ctx.fill()
      })
    }

  }, [canvasSize, workspace, zones, state, selectedZone, dragPreview])

  // Draw side-view canvas
  useEffect(() => {
    const canvas = sideCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const sideWidth = canvasSize.width
    const sideHeight = 120

    canvas.width = sideWidth
    canvas.height = sideHeight

    drawSideView(ctx, workspace, sideWidth, sideHeight, zones)
  }, [canvasSize, workspace, zones])

  // Grid drawing
  function drawGrid(
    ctx: CanvasRenderingContext2D,
    ws: WorkspaceConfig,
    size: { width: number; height: number },
    zoom: number,
    pan: Point2D
  ) {
    const scale = getScale(ws, size.width, size.height, zoom)

    // Determine grid spacing based on zoom
    let gridSpacing = 1000 // 1m
    if (scale * gridSpacing < 20) gridSpacing = 5000
    if (scale * gridSpacing > 100) gridSpacing = 500
    if (scale * gridSpacing > 200) gridSpacing = 100

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'
    ctx.lineWidth = 1

    // Vertical lines
    for (let x = 0; x <= ws.width; x += gridSpacing) {
      const p1 = worldToCanvas({ x, y: 0 }, ws, size.width, size.height, zoom, pan)
      const p2 = worldToCanvas({ x, y: ws.height }, ws, size.width, size.height, zoom, pan)
      ctx.beginPath()
      ctx.moveTo(p1.x, p1.y)
      ctx.lineTo(p2.x, p2.y)
      ctx.stroke()
    }

    // Horizontal lines
    for (let y = 0; y <= ws.height; y += gridSpacing) {
      const p1 = worldToCanvas({ x: 0, y }, ws, size.width, size.height, zoom, pan)
      const p2 = worldToCanvas({ x: ws.width, y }, ws, size.width, size.height, zoom, pan)
      ctx.beginPath()
      ctx.moveTo(p1.x, p1.y)
      ctx.lineTo(p2.x, p2.y)
      ctx.stroke()
    }

    // Workspace border
    const corners = [
      worldToCanvas({ x: 0, y: 0 }, ws, size.width, size.height, zoom, pan),
      worldToCanvas({ x: ws.width, y: 0 }, ws, size.width, size.height, zoom, pan),
      worldToCanvas({ x: ws.width, y: ws.height }, ws, size.width, size.height, zoom, pan),
      worldToCanvas({ x: 0, y: ws.height }, ws, size.width, size.height, zoom, pan),
    ]

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(corners[0].x, corners[0].y)
    corners.forEach(c => ctx.lineTo(c.x, c.y))
    ctx.closePath()
    ctx.stroke()

    // Dimension labels
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
    ctx.font = '11px sans-serif'

    // Width label
    const widthMid = worldToCanvas({ x: ws.width / 2, y: 0 }, ws, size.width, size.height, zoom, pan)
    ctx.textAlign = 'center'
    ctx.fillText(formatDistance(ws.width), widthMid.x, widthMid.y + 20)

    // Height label
    const heightMid = worldToCanvas({ x: 0, y: ws.height / 2 }, ws, size.width, size.height, zoom, pan)
    ctx.save()
    ctx.translate(heightMid.x - 20, heightMid.y)
    ctx.rotate(-Math.PI / 2)
    ctx.textAlign = 'center'
    ctx.fillText(formatDistance(ws.height), 0, 0)
    ctx.restore()
  }

  // LIDAR range circle
  function drawLidarRange(
    ctx: CanvasRenderingContext2D,
    ws: WorkspaceConfig,
    size: { width: number; height: number },
    zoom: number,
    pan: Point2D,
    lidarPos: { x: number; y: number }
  ) {
    const lidarCanvas = worldToCanvas(
      lidarPos,
      ws, size.width, size.height, zoom, pan
    )

    const scale = getScale(ws, size.width, size.height, zoom)
    const radiusPx = LIDAR_MAX_RANGE * scale

    ctx.beginPath()
    ctx.arc(lidarCanvas.x, lidarCanvas.y, radiusPx, 0, Math.PI * 2)
    ctx.strokeStyle = 'rgba(20, 184, 166, 0.3)'
    ctx.lineWidth = 1
    ctx.setLineDash([5, 5])
    ctx.stroke()
    ctx.setLineDash([])

    // If tilt != 0, show effective range circle (projected)
    if (ws.lidar_tilt !== 0) {
      const tiltCos = Math.cos((ws.lidar_tilt * Math.PI) / 180)
      const effectiveRadiusPx = LIDAR_MAX_RANGE * tiltCos * scale

      ctx.beginPath()
      ctx.arc(lidarCanvas.x, lidarCanvas.y, effectiveRadiusPx, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(234, 179, 8, 0.3)'
      ctx.lineWidth = 1
      ctx.setLineDash([3, 3])
      ctx.stroke()
      ctx.setLineDash([])

      // Label for effective range
      ctx.fillStyle = 'rgba(234, 179, 8, 0.5)'
      ctx.font = '9px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(
        `eff. ${formatDistance(Math.round(LIDAR_MAX_RANGE * tiltCos))}`,
        lidarCanvas.x,
        lidarCanvas.y - effectiveRadiusPx - 5
      )
    }
  }

  // LIDAR marker
  function drawLidarMarker(
    ctx: CanvasRenderingContext2D,
    ws: WorkspaceConfig,
    size: { width: number; height: number },
    zoom: number,
    pan: Point2D,
    lidarPos: { x: number; y: number }
  ) {
    const lidarCanvas = worldToCanvas(
      lidarPos,
      ws, size.width, size.height, zoom, pan
    )

    // Outer circle
    ctx.beginPath()
    ctx.arc(lidarCanvas.x, lidarCanvas.y, 20, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(20, 184, 166, 0.8)'
    ctx.fill()
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = 2
    ctx.stroke()

    // Inner circle
    ctx.beginPath()
    ctx.arc(lidarCanvas.x, lidarCanvas.y, 8, 0, Math.PI * 2)
    ctx.fillStyle = '#fff'
    ctx.fill()

    // Direction indicator (0° = up, 90° = right)
    const rad = (ws.lidar_rotation * Math.PI) / 180
    const dirX = lidarCanvas.x + Math.sin(rad) * 30
    const dirY = lidarCanvas.y - Math.cos(rad) * 30

    ctx.beginPath()
    ctx.moveTo(lidarCanvas.x, lidarCanvas.y)
    ctx.lineTo(dirX, dirY)
    ctx.strokeStyle = 'rgba(20, 184, 166, 1)'
    ctx.lineWidth = 3
    ctx.stroke()

    // Arrow head
    ctx.beginPath()
    ctx.arc(dirX, dirY, 4, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(20, 184, 166, 1)'
    ctx.fill()

    // Label
    ctx.fillStyle = '#fff'
    ctx.font = 'bold 10px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('LIDAR', lidarCanvas.x, lidarCanvas.y + 35)

    // Tilt indicator arc (if tilt > 0)
    if (ws.lidar_tilt !== 0) {
      const absTilt = Math.abs(ws.lidar_tilt)
      const tiltRad = (absTilt * Math.PI) / 180
      ctx.beginPath()
      ctx.arc(lidarCanvas.x, lidarCanvas.y, 25, -Math.PI / 2 - tiltRad / 2, -Math.PI / 2 + tiltRad / 2)
      ctx.strokeStyle = 'rgba(234, 179, 8, 0.8)'
      ctx.lineWidth = 2
      ctx.stroke()

      // Tilt label with sign
      ctx.fillStyle = 'rgba(234, 179, 8, 0.9)'
      ctx.font = '9px sans-serif'
      const tiltSign = ws.lidar_tilt > 0 ? '↑' : '↓'
      ctx.fillText(`${tiltSign}${ws.lidar_tilt}°`, lidarCanvas.x, lidarCanvas.y + 46)
    }
  }

  // Side-view elevation drawing (shows tilt cross-section)
  function drawSideView(
    ctx: CanvasRenderingContext2D,
    ws: WorkspaceConfig,
    width: number,
    height: number,
    zoneList: Zone[]
  ) {
    const padding = 30
    const drawWidth = width - padding * 2
    const drawHeight = height - padding * 2

    // Clear
    ctx.fillStyle = '#1a1a2e'
    ctx.fillRect(0, 0, width, height)

    // Title
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)'
    ctx.font = 'bold 11px sans-serif'
    ctx.textAlign = 'left'
    ctx.fillText('Vista laterale (tilt)', padding, 16)

    const absTilt = Math.abs(ws.lidar_tilt)
    const tiltRad = (absTilt * Math.PI) / 180
    const tiltSign = ws.lidar_tilt >= 0 ? 1 : -1  // 1=up, -1=down
    const tiltCos = Math.cos(tiltRad)

    // Calculate scale: we show a side profile from 0 to max_range horizontal
    const maxRange = LIDAR_MAX_RANGE
    const maxHeight = maxRange * Math.sin(tiltRad) + 500 // extra margin
    const scaleX = drawWidth / maxRange
    const scaleY = drawHeight / Math.max(maxHeight, 2000)
    const scale = Math.min(scaleX, scaleY)

    // Origin: LIDAR position (left side, vertically centered)
    const originX = padding + 20
    const originY = ws.lidar_tilt >= 0
      ? height - padding - 10
      : padding + 20  // If tilt is negative (downward), put LIDAR at top

    // Ground/reference line
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(padding, originY)
    ctx.lineTo(width - padding, originY)
    ctx.stroke()

    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)'
    ctx.font = '9px sans-serif'
    ctx.textAlign = 'left'
    ctx.fillText('piano orizzontale', padding + 5, originY + (tiltSign >= 0 ? 12 : -5))

    if (ws.lidar_tilt !== 0) {
      // Draw tilted scan line (upward or downward)
      const scanEndX = originX + maxRange * tiltCos * scale
      const scanEndY = originY - tiltSign * maxRange * Math.sin(tiltRad) * scale

      // Scan cone (main beam line)
      ctx.beginPath()
      ctx.moveTo(originX, originY)
      ctx.lineTo(scanEndX, scanEndY)
      ctx.strokeStyle = 'rgba(20, 184, 166, 0.6)'
      ctx.lineWidth = 2
      ctx.setLineDash([5, 3])
      ctx.stroke()
      ctx.setLineDash([])

      // Tilt angle arc
      const arcRadius = 50
      if (tiltSign >= 0) {
        ctx.beginPath()
        ctx.arc(originX, originY, arcRadius, 0, -tiltRad, true)
        ctx.strokeStyle = 'rgba(234, 179, 8, 0.8)'
        ctx.lineWidth = 2
        ctx.stroke()
      } else {
        ctx.beginPath()
        ctx.arc(originX, originY, arcRadius, 0, tiltRad, false)
        ctx.strokeStyle = 'rgba(234, 179, 8, 0.8)'
        ctx.lineWidth = 2
        ctx.stroke()
      }

      // Tilt angle label
      ctx.fillStyle = 'rgba(234, 179, 8, 0.9)'
      ctx.font = 'bold 11px sans-serif'
      ctx.textAlign = 'left'
      const labelY = originY - tiltSign * arcRadius * Math.sin(tiltRad / 2) + 4
      ctx.fillText(`${ws.lidar_tilt}°`, originX + arcRadius + 5, labelY)

      // Horizontal reference line
      ctx.beginPath()
      ctx.moveTo(originX, originY)
      ctx.lineTo(originX + maxRange * tiltCos * scale, originY)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)'
      ctx.lineWidth = 1
      ctx.setLineDash([3, 3])
      ctx.stroke()
      ctx.setLineDash([])

      // Draw zone distance ranges on the tilted beam
      zoneList.forEach((zone, index) => {
        if (!zone.enabled) return
        const colors = ZONE_COLORS_LIST[index % ZONE_COLORS_LIST.length]

        // Zone distance range on the tilted plane
        const dMin = zone.distance_min
        const dMax = zone.distance_max

        // On tilted plane
        const x1 = originX + dMin * tiltCos * scale
        const y1 = originY - tiltSign * dMin * Math.sin(tiltRad) * scale
        const x2 = originX + dMax * tiltCos * scale
        const y2 = originY - tiltSign * dMax * Math.sin(tiltRad) * scale

        // Draw zone segment on beam
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.strokeStyle = colors.stroke
        ctx.lineWidth = 6
        ctx.lineCap = 'round'
        ctx.stroke()
        ctx.lineCap = 'butt'

        // Projected range on ground
        const projMin = dMin * tiltCos
        const projMax = dMax * tiltCos
        const gx1 = originX + projMin * scale
        const gx2 = originX + projMax * scale

        // Projection lines (dashed)
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(gx1, originY)
        ctx.moveTo(x2, y2)
        ctx.lineTo(gx2, originY)
        ctx.strokeStyle = colors.stroke.replace('rgb', 'rgba').replace(')', ', 0.3)')
        ctx.lineWidth = 1
        ctx.setLineDash([2, 2])
        ctx.stroke()
        ctx.setLineDash([])

        // Ground projection bar
        const barOffset = tiltSign >= 0 ? 3 : -3
        ctx.beginPath()
        ctx.moveTo(gx1, originY + barOffset)
        ctx.lineTo(gx2, originY + barOffset)
        ctx.strokeStyle = colors.stroke
        ctx.lineWidth = 3
        ctx.stroke()

        // Zone name
        const midX = (x1 + x2) / 2
        const midY = (y1 + y2) / 2
        ctx.fillStyle = colors.stroke
        ctx.font = '9px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText(zone.name, midX, midY - tiltSign * 8)
      })

      // LIDAR marker
      ctx.beginPath()
      ctx.arc(originX, originY, 8, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(20, 184, 166, 0.8)'
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 2
      ctx.stroke()

      // Info text
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
      ctx.font = '9px sans-serif'
      ctx.textAlign = 'right'
      ctx.fillText(
        `Copertura orizz. = ${Math.round(tiltCos * 100)}% | Raggio eff. ${formatDistance(Math.round(maxRange * tiltCos))}`,
        width - padding,
        16
      )
    } else {
      // No tilt - show flat scan line
      const scanEndX = originX + maxRange * scale
      ctx.beginPath()
      ctx.moveTo(originX, originY)
      ctx.lineTo(scanEndX, originY)
      ctx.strokeStyle = 'rgba(20, 184, 166, 0.6)'
      ctx.lineWidth = 2
      ctx.stroke()

      // Zone segments on the flat line
      zoneList.forEach((zone, index) => {
        if (!zone.enabled) return
        const colors = ZONE_COLORS_LIST[index % ZONE_COLORS_LIST.length]
        const x1 = originX + zone.distance_min * scale
        const x2 = originX + zone.distance_max * scale

        ctx.beginPath()
        ctx.moveTo(x1, originY)
        ctx.lineTo(x2, originY)
        ctx.strokeStyle = colors.stroke
        ctx.lineWidth = 6
        ctx.lineCap = 'round'
        ctx.stroke()
        ctx.lineCap = 'butt'

        ctx.fillStyle = colors.stroke
        ctx.font = '9px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText(zone.name, (x1 + x2) / 2, originY - 8)
      })

      // LIDAR marker
      ctx.beginPath()
      ctx.arc(originX, originY, 8, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(20, 184, 166, 0.8)'
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 2
      ctx.stroke()

      // Info
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
      ctx.font = '9px sans-serif'
      ctx.textAlign = 'right'
      ctx.fillText('Tilt: 0° (orizzontale)', width - padding, 16)
    }
  }

  // Get world position from mouse event
  const getWorldPos = useCallback((e: React.MouseEvent<HTMLCanvasElement>): Point2D => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()
    const canvasX = e.clientX - rect.left
    const canvasY = e.clientY - rect.top

    return canvasToWorld(
      { x: canvasX, y: canvasY },
      workspace,
      canvasSize.width,
      canvasSize.height,
      state.zoom,
      state.panOffset
    )
  }, [workspace, canvasSize, state.zoom, state.panOffset])

  // Find zone at position
  const findZoneAtPos = useCallback((worldPos: Point2D): Zone | null => {
    for (let i = zones.length - 1; i >= 0; i--) {
      const zone = zones[i]
      const points = zone.geometry_points || []
      if (points.length >= 3 && isPointInPolygon(worldPos, points)) {
        return zone
      }
    }
    return null
  }, [zones])

  // Check if mouse is near LIDAR
  const isNearLidar = useCallback((worldPos: Point2D): boolean => {
    const scale = getScale(workspace, canvasSize.width, canvasSize.height, state.zoom)
    const threshold = 30 / scale // 30px in world coords
    return distance(worldPos, { x: workspace.lidar_x, y: workspace.lidar_y }) < threshold
  }, [workspace, canvasSize, state.zoom])

  // Find handle at position (returns index or -1)
  const findHandleAtPos = useCallback((worldPos: Point2D, zone: Zone | null): number => {
    if (!zone || !zone.geometry_points) return -1

    const scale = getScale(workspace, canvasSize.width, canvasSize.height, state.zoom)
    const threshold = 10 / scale // 10px in world coords

    for (let i = 0; i < zone.geometry_points.length; i++) {
      const p = zone.geometry_points[i]
      if (distance(worldPos, p) < threshold) {
        return i
      }
    }
    return -1
  }, [workspace, canvasSize, state.zoom])

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const worldPos = getWorldPos(e)

    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      // Middle button or Alt+left: start panning
      setState(prev => ({
        ...prev,
        draggedElement: null,
        dragOffset: { x: e.clientX - prev.panOffset.x, y: e.clientY - prev.panOffset.y }
      }))
      return
    }

    if (state.tool === 'select') {
      // Check if clicking on LIDAR
      if (isNearLidar(worldPos)) {
        dragStateRef.current = {
          element: 'lidar',
          handleIndex: -1,
          startPos: { x: worldPos.x - workspace.lidar_x, y: worldPos.y - workspace.lidar_y },
          zone: null
        }
        setState(prev => ({
          ...prev,
          draggedElement: 'lidar',
          dragOffset: { x: worldPos.x - workspace.lidar_x, y: worldPos.y - workspace.lidar_y }
        }))
        return
      }

      // Check if clicking on a handle of selected zone
      if (state.selectedZone) {
        // Get the current zone data from props (updated by server)
        const currentZone = zones.find(z => z.id === state.selectedZone?.id) || state.selectedZone
        const handleIndex = findHandleAtPos(worldPos, currentZone)
        if (handleIndex >= 0) {
          dragStateRef.current = {
            element: 'handle',
            handleIndex: handleIndex,
            startPos: worldPos,
            zone: currentZone  // Use fresh zone data
          }
          setState(prev => ({
            ...prev,
            draggedElement: 'handle',
            dragHandleIndex: handleIndex,
            dragOffset: worldPos
          }))
          return
        }
      }

      // Check if clicking on a zone
      const zone = findZoneAtPos(worldPos)
      if (zone) {
        // Get the current zone data from props (updated by server)
        const currentZone = zones.find(z => z.id === zone.id) || zone
        dragStateRef.current = {
          element: 'zone',
          handleIndex: -1,
          startPos: worldPos,
          zone: currentZone  // Use fresh zone data
        }
        onSelectZone(currentZone)
        setState(prev => ({
          ...prev,
          selectedZone: currentZone,
          draggedElement: 'zone',
          dragOffset: worldPos,
          dragHandleIndex: -1
        }))
      } else {
        dragStateRef.current = { element: null, handleIndex: -1, startPos: { x: 0, y: 0 }, zone: null }
        onSelectZone(null)
        setState(prev => ({ ...prev, selectedZone: null, dragHandleIndex: -1 }))
      }
    } else if (state.tool === 'rectangle') {
      setState(prev => ({
        ...prev,
        isDrawing: true,
        drawPoints: [worldPos]
      }))
    } else if (state.tool === 'polygon') {
      if (!state.isDrawing) {
        setState(prev => ({
          ...prev,
          isDrawing: true,
          drawPoints: [worldPos]
        }))
      } else {
        // Check if closing the polygon
        if (state.drawPoints.length >= 3) {
          const firstPoint = state.drawPoints[0]
          const scale = getScale(workspace, canvasSize.width, canvasSize.height, state.zoom)
          const closeThreshold = 20 / scale
          if (distance(worldPos, firstPoint) < closeThreshold) {
            // Close polygon
            setPendingPoints(state.drawPoints)
            setShowNameDialog(true)
            setState(prev => ({
              ...prev,
              isDrawing: false,
              drawPoints: []
            }))
            return
          }
        }
        setState(prev => ({
          ...prev,
          drawPoints: [...prev.drawPoints, worldPos]
        }))
      }
    } else if (state.tool === 'move-lidar') {
      onUpdateWorkspace({
        lidar_x: Math.round(worldPos.x),
        lidar_y: Math.round(worldPos.y)
      })
    }
  }, [getWorldPos, state.tool, state.isDrawing, state.drawPoints, state.selectedZone, zones, isNearLidar, findZoneAtPos, findHandleAtPos, onSelectZone, onUpdateWorkspace, workspace, canvasSize])

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const worldPos = getWorldPos(e)

    // Panning (middle button or alt+drag)
    if (e.buttons === 4 || (e.buttons === 1 && e.altKey)) {
      setState(prev => ({
        ...prev,
        panOffset: {
          x: e.clientX - prev.dragOffset.x,
          y: e.clientY - prev.dragOffset.y
        }
      }))
      return
    }

    const drag = dragStateRef.current

    // Dragging LIDAR (preview only)
    if (drag.element === 'lidar') {
      setDragPreview({
        lidar: {
          x: Math.round(worldPos.x - drag.startPos.x),
          y: Math.round(worldPos.y - drag.startPos.y)
        }
      })
      return
    }

    // Dragging handle (preview only)
    if (drag.element === 'handle' && drag.zone && drag.handleIndex >= 0) {
      const points = [...(drag.zone.geometry_points || [])]
      if (drag.handleIndex < points.length) {
        points[drag.handleIndex] = { x: worldPos.x, y: worldPos.y }
        setDragPreview({ zonePoints: points })
      }
      return
    }

    // Dragging zone (preview only)
    if (drag.element === 'zone' && drag.zone) {
      const dx = worldPos.x - drag.startPos.x
      const dy = worldPos.y - drag.startPos.y
      const points = drag.zone.geometry_points || []
      const newPoints = points.map(p => ({
        x: p.x + dx,
        y: p.y + dy
      }))
      setDragPreview({ zonePoints: newPoints })
      // Don't update startPos - always calculate from original position
      return
    }

    // Drawing rectangle
    if (state.isDrawing && state.tool === 'rectangle') {
      setState(prev => ({
        ...prev,
        drawPoints: [prev.drawPoints[0], worldPos]
      }))
      return
    }

    // Hover detection
    if (state.tool === 'select') {
      // Check for handle hover on selected zone
      const handleIndex = findHandleAtPos(worldPos, state.selectedZone)
      const zone = findZoneAtPos(worldPos)
      setState(prev => ({
        ...prev,
        hoveredZone: zone,
        hoveredHandle: handleIndex
      }))
    }
  }, [getWorldPos, state.isDrawing, state.tool, findZoneAtPos, findHandleAtPos])

  const handleMouseUp = useCallback(() => {
    const drag = dragStateRef.current

    // Commit LIDAR position
    if (drag.element === 'lidar' && dragPreview?.lidar) {
      onUpdateWorkspace({
        lidar_x: dragPreview.lidar.x,
        lidar_y: dragPreview.lidar.y
      })
    }

    // Commit zone/handle position
    if ((drag.element === 'zone' || drag.element === 'handle') &&
        drag.zone && dragPreview?.zonePoints) {
      onUpdateZone({
        ...drag.zone,
        geometry_points: dragPreview.zonePoints
      })
    }

    // Complete rectangle drawing
    if (state.isDrawing && state.tool === 'rectangle' && state.drawPoints.length === 2) {
      const rectPoints = createRectangle(state.drawPoints[0], state.drawPoints[1])
      setPendingPoints(rectPoints)
      setShowNameDialog(true)
      setState(prev => ({
        ...prev,
        isDrawing: false,
        drawPoints: []
      }))
    }

    // Reset drag state
    dragStateRef.current = { element: null, handleIndex: -1, startPos: { x: 0, y: 0 }, zone: null }
    setDragPreview(null)
    setState(prev => ({
      ...prev,
      draggedElement: null,
      dragHandleIndex: -1
    }))
  }, [state.isDrawing, state.tool, state.drawPoints, dragPreview, onUpdateWorkspace, onUpdateZone])

  const handleWheel = useCallback((e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    setState(prev => ({
      ...prev,
      zoom: Math.max(0.1, Math.min(5, prev.zoom * delta))
    }))
  }, [])

  const handleDoubleClick = useCallback(() => {
    if (state.tool === 'polygon' && state.isDrawing && state.drawPoints.length >= 3) {
      setPendingPoints(state.drawPoints)
      setShowNameDialog(true)
      setState(prev => ({
        ...prev,
        isDrawing: false,
        drawPoints: []
      }))
    }
  }, [state.tool, state.isDrawing, state.drawPoints])

  const handleCreateZone = useCallback(() => {
    if (pendingPoints.length >= 3 && newZoneName.trim()) {
      onCreateZone(pendingPoints, newZoneName.trim())
      setNewZoneName('')
      setShowNameDialog(false)
      setPendingPoints([])
    }
  }, [pendingPoints, newZoneName, onCreateZone])

  const handleCancelCreate = useCallback(() => {
    setNewZoneName('')
    setShowNameDialog(false)
    setPendingPoints([])
  }, [])

  const handleDeleteSelected = useCallback(() => {
    if (state.selectedZone) {
      onDeleteZone(state.selectedZone.id)
      setState(prev => ({ ...prev, selectedZone: null }))
      onSelectZone(null)
    }
  }, [state.selectedZone, onDeleteZone, onSelectZone])

  return (
    <div className="flex flex-col h-full bg-gray-900 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-1 mr-4">
          <button
            onClick={() => setState(prev => ({ ...prev, tool: 'select' }))}
            className={`p-2 rounded ${state.tool === 'select' ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            title="Seleziona (V)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
          </button>
          <button
            onClick={() => setState(prev => ({ ...prev, tool: 'rectangle' }))}
            className={`p-2 rounded ${state.tool === 'rectangle' ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            title="Rettangolo (R)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
            </svg>
          </button>
          <button
            onClick={() => setState(prev => ({ ...prev, tool: 'polygon' }))}
            className={`p-2 rounded ${state.tool === 'polygon' ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            title="Poligono (P)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <polygon points="12,2 22,8.5 18,21 6,21 2,8.5" strokeWidth={2} />
            </svg>
          </button>
          <button
            onClick={() => setState(prev => ({ ...prev, tool: 'move-lidar' }))}
            className={`p-2 rounded ${state.tool === 'move-lidar' ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            title="Sposta LIDAR (L)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="3" strokeWidth={2} />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v4m0 12v4m10-10h-4M6 12H2" />
            </svg>
          </button>
        </div>

        <div className="h-6 w-px bg-gray-600" />

        <div className="flex items-center gap-1 mx-2">
          <button
            onClick={() => setState(prev => ({ ...prev, zoom: Math.min(5, prev.zoom * 1.2) }))}
            className="p-2 rounded bg-gray-700 hover:bg-gray-600"
            title="Zoom +"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
            </svg>
          </button>
          <span className="text-sm text-gray-400 w-16 text-center">{Math.round(state.zoom * 100)}%</span>
          <button
            onClick={() => setState(prev => ({ ...prev, zoom: Math.max(0.1, prev.zoom / 1.2) }))}
            className="p-2 rounded bg-gray-700 hover:bg-gray-600"
            title="Zoom -"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM7 10h6" />
            </svg>
          </button>
          <button
            onClick={() => setState(prev => ({ ...prev, zoom: 1, panOffset: { x: 0, y: 0 } }))}
            className="p-2 rounded bg-gray-700 hover:bg-gray-600"
            title="Reset vista"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        <div className="flex-1" />

        {state.selectedZone && (
          <button
            onClick={handleDeleteSelected}
            className="p-2 rounded bg-red-600 hover:bg-red-500"
            title="Elimina zona selezionata"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}

        <div className="text-xs text-gray-400 ml-4">
          Area: {formatDistance(workspace.width)} × {formatDistance(workspace.height)} |
          LIDAR: ({formatDistance(workspace.lidar_x)}, {formatDistance(workspace.lidar_y)}) |
          Rot: {workspace.lidar_rotation}° |
          Tilt: {workspace.lidar_tilt}°
        </div>
      </div>

      {/* Canvas */}
      <div ref={containerRef} className="flex-1 relative" style={{ minHeight: 0 }}>
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          className="absolute inset-0"
          style={{
            cursor: dragStateRef.current.element
              ? (dragStateRef.current.element === 'handle' ? 'grabbing' : 'move')
              : state.hoveredHandle >= 0
                ? 'grab'
                : state.hoveredZone
                  ? 'move'
                  : state.tool === 'select'
                    ? 'default'
                    : 'crosshair'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onWheel={handleWheel}
          onDoubleClick={handleDoubleClick}
          onContextMenu={e => e.preventDefault()}
        />

        {/* Instructions overlay */}
        {state.tool === 'polygon' && state.isDrawing && (
          <div className="absolute bottom-4 left-4 bg-gray-800/90 px-3 py-2 rounded text-sm">
            Clicca per aggiungere punti. Doppio click o clicca sul primo punto per chiudere.
          </div>
        )}

        {state.tool === 'select' && state.selectedZone && !state.draggedElement && (
          <div className="absolute bottom-4 left-4 bg-gray-800/90 px-3 py-2 rounded text-sm">
            Trascina la zona per spostarla. Trascina i vertici per ridimensionare.
          </div>
        )}

        {/* Name dialog */}
        {/* Side-view elevation panel */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-700">
          <canvas
            ref={sideCanvasRef}
            width={canvasSize.width}
            height={120}
            className="w-full"
            style={{ height: '120px' }}
          />
        </div>

        {showNameDialog && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-gray-800 rounded-lg p-6 w-80">
              <h3 className="text-lg font-semibold mb-4">Nome Zona</h3>
              <input
                type="text"
                value={newZoneName}
                onChange={e => setNewZoneName(e.target.value)}
                placeholder="Inserisci nome zona..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded mb-4 focus:outline-none focus:border-blue-500"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCreateZone()
                  if (e.key === 'Escape') handleCancelCreate()
                }}
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={handleCancelCreate}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded"
                >
                  Annulla
                </button>
                <button
                  onClick={handleCreateZone}
                  disabled={!newZoneName.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Crea
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ZoneEditor2D
